// Copyright (c) StrangeBee 2024

import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Layout } from 'antd';
import { classList } from '@Helpers/className';
import { CollapseContext } from './context';
import { useCollapse } from './hook';
import useLocalStorage, { LocalStorageKeys } from '@Hooks/useLocalStorage';

import './THLayout.scss';

const { Content: AntContent, Sider: AntSider } = Layout;

interface StorageCollapseType {
    opened: string[];
}

interface UseStorageCollapseOutput {
    collapse: StorageCollapseType;
    setCollapse: React.Dispatch<React.SetStateAction<StorageCollapseType>>;
}

interface ProviderProps {
    children: ReactElement;
    initialState?: {
        collapsed?: boolean;
        hasSider?: boolean;
    };
}

export const useStorageCollapse = (): UseStorageCollapseOutput => {
    const { storage, setStorage } = useLocalStorage<StorageCollapseType>({
        key: LocalStorageKeys.COLLAPSE,
        initialValue: { opened: [] },
    });

    const [collapse, setCollapse] = useState<StorageCollapseType>(storage);

    useEffect(() => {
        setStorage((state) => ({ ...state, ...collapse }));
    }, [collapse, setStorage]);

    return { collapse, setCollapse };
};

const Provider = ({ children, initialState }: ProviderProps): ReactElement => {
    const { collapsed: defaultCollapsed = true, hasSider: defaultHasSider = false } = initialState || {};
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    const [hasSider, setHasSider] = useState(defaultHasSider);

    return (
        <CollapseContext.Provider value={{ collapsed, setCollapsed, hasSider, setHasSider }}>
            {children}
        </CollapseContext.Provider>
    );
};

interface ContentProps {
    children: ReactNode;
    className?: string;
}

const Content = ({ children, className }: ContentProps): ReactElement => (
    <AntContent className="th-layout__content">
        <div className={classList('th-layout__content-paper', className)}>{children}</div>
    </AntContent>
);

Content.displayName = 'Content';
interface SiderProps {
    children: ReactElement;
    className?: string;
    onChange?: (collapsed: boolean) => void;
    width?: string | number;
    initialCollapse?: boolean;
    name?: string;
}

const Sider = ({
    children,
    onChange,
    width = '33%',
    className,
    initialCollapse = true,
    name,
}: SiderProps): ReactElement => {
    const { collapsed, setCollapsed } = useCollapse();

    const { setCollapse } = useStorageCollapse();

    useEffect(() => {
        onChange?.(collapsed);
        if (name) {
            setCollapse((st) => {
                return {
                    opened: collapsed ? [...st.opened, name] : st.opened.filter((current) => name !== current),
                };
            });
        }
    }, [collapsed, name, onChange, setCollapse]);

    useEffect(() => {
        setCollapsed(initialCollapse);
    }, [initialCollapse]); // eslint-disable-line

    return (
        <AntSider
            className={classList('th-layout__sider', className)}
            collapsible
            width={width}
            collapsedWidth="1px"
            collapsed={collapsed}
            onCollapse={(collapsed): void => setCollapsed(collapsed)}
            reverseArrow
        >
            {children}
        </AntSider>
    );
};

Sider.displayName = 'Sider';

interface THLayoutProps {
    children: ReactNode;
}

export const THLayout = ({ children }: THLayoutProps): ReactElement => {
    const { setHasSider } = useCollapse();

    useEffect(() => {
        setHasSider(Array.isArray(children));
    }, [setHasSider, children]); // used to avoid a React warning

    if (!Array.isArray(children)) {
        return <Content>{children}</Content>;
    }

    try {
        const ContentElem = (children as ReactElement[]).find(({ type }: any) => type?.displayName === 'Content');
        const SiderElem = (children as ReactElement[]).find(({ type }: any) => type?.displayName === 'Sider');

        if (!ContentElem || !SiderElem) {
            throw new Error('Content or Sider undefined!');
        }

        return (
            <>
                {ContentElem}
                {SiderElem}
            </>
        );
    } catch (e) {
        console.error(e); // eslint-disable-line
        return <></>;
    }
};

THLayout.Provider = Provider;
THLayout.Content = Content;
THLayout.Sider = Sider;
